import { logError, ErrorSeverity } from "@/lib/errorLogger";

export type DatabaseOperation = "select" | "insert" | "update" | "delete" | "invoke";

export interface DatabaseErrorContext {
  user_id?: string;
  company_id?: string;
  entity_type?: string;
  entity_id?: string;
  entity_name?: string;
  operation?: DatabaseOperation;
  table?: string;
  function_name?: string;
}

/**
 * Log database errors to the error_logs table via the log-error edge function.
 * Use this in catch blocks when handling Supabase errors.
 */
export async function logDatabaseError(
  error: Error | { message: string } | string,
  source: string,
  context?: DatabaseErrorContext
): Promise<void> {
  const errorMessage = typeof error === "string" 
    ? error 
    : error instanceof Error 
      ? error.message 
      : error.message;

  // Determine severity based on error message
  let severity: ErrorSeverity = "error";
  if (errorMessage.includes("violates foreign key constraint")) {
    severity = "critical";
  } else if (errorMessage.includes("violates row-level security")) {
    severity = "critical";
  } else if (errorMessage.includes("permission denied")) {
    severity = "critical";
  } else if (errorMessage.includes("duplicate key")) {
    severity = "warning";
  }

  await logError(errorMessage, source, {
    error_type: "database",
    severity,
    user_id: context?.user_id,
    company_id: context?.company_id,
    details: {
      operation: context?.operation,
      entity_type: context?.entity_type,
      entity_id: context?.entity_id,
      entity_name: context?.entity_name,
      table: context?.table,
      function_name: context?.function_name,
    },
  });
}

/**
 * Log edge function invocation errors.
 */
export async function logEdgeFunctionError(
  error: Error | { message: string } | string,
  functionName: string,
  context?: Omit<DatabaseErrorContext, "operation" | "table" | "function_name">
): Promise<void> {
  const errorMessage = typeof error === "string"
    ? error
    : error instanceof Error
      ? error.message
      : error.message;

  await logError(errorMessage, `EdgeFunction:${functionName}`, {
    error_type: "edge_function",
    severity: "error",
    user_id: context?.user_id,
    company_id: context?.company_id,
    details: {
      function_name: functionName,
      entity_type: context?.entity_type,
      entity_id: context?.entity_id,
      entity_name: context?.entity_name,
    },
  });
}
